/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {translate} from '@ion/core/src/locale/locale';
import {
  AccountLink,
  Entity
} from '@ion/core/src/types/entities';
import {
  DownloadTemplate
} from '@ion/core/src/types/templates';
import {
  DeviceTemplateOptions,
  exportMapper
} from '@ion/core/src/xlsx/DeviceTemplate';
import {
  DownloadTemplateDeviceTypes
} from '@ion/core/src/xlsx/TemplateHelpers';
import {BulkUploadDialog} from '@ion/dls/src/imports/BulkUploadDialog';
import {importDevicesValidation} from '../../store/actions/importer';
import {onImportStart} from './ImportDevicesContainerUtils';
import {importAdminDevices} from './state';

// defines the prop definitions for import devices container ...
export interface ImportDevicesContainerProps extends DownloadTemplateDeviceTypes {
  onDownload: (params?: DownloadTemplate) => void;
  account?: AccountLink;
  hasAddPermission?: boolean;
};

// defines the override styles ...
const overrides = () => ({
  formPaper : {
    minWidth    : '70% !important',
    paddingLeft : '2px !important',
    maxHeight   : '574px',
    height      : '574px'
  }
});

/**
 * This component acts as a container to initiate bulk device import ...
 */
export const ImportDevicesContainer = ({
  onDownload,
  account,
  isDeviceImport = false,
  hasAddPermission = true
}: ImportDevicesContainerProps) => {
  const dispatch                              = useDispatch();
  const itemsRef                              = useRef();
  const importResults                         = useSelector(({importer: {current}}) => current);
  const [templateOptions, setTemplateOptions] = useState<DeviceTemplateOptions>({
    groupName : account?.title
  });

  const onConfirm = useCallback(() => {
    dispatch(importAdminDevices(false, importResults?.result));
  }, [importResults]);

  const onClose = useCallback(() => {
    dispatch(importAdminDevices(false, null));
  }, []);

  const clearImportValidation = useCallback(() => {
    dispatch(importDevicesValidation(false));
  }, []);

  const handleSubmit = useCallback(() => {
    onImportStart({
      items : itemsRef.current,
      account,
      dispatch
    });
  }, []);

  const itemsUpdate = useCallback((items) => {
    itemsRef.current = items;
  }, []);

  const handleDownload = useCallback((params?: DownloadTemplate) => {
    onDownload?.(params);
  }, []);

  const title = useMemo(() => {
    return translate(`common:editors.import.${hasAddPermission ? 'title' : 'titleUpdate'}`, {
      entity : translate('common:entityLabels.devices')
    });
  }, [hasAddPermission]);

  return (
    <BulkUploadDialog open
      dialogTitle={title}
      onClose={onClose}
      onCancel={onClose}
      isDeviceImport={isDeviceImport}
      styleOverrides={overrides}
      onImport={handleSubmit}
      onDownload={handleDownload}
      onConfirm={onConfirm}
      exportMapper={exportMapper}
      entity={Entity.device}
      entityLabel={translate('common:entityLabels.devices')}
      templateSrcPath={'admin-devices'}
      templateOptions={templateOptions}
      isLoading={false}
      itemsUpdate={itemsUpdate}
      clearImportValidation={clearImportValidation}
      isDisabled={false}
      omitActions={false}
      importResults={importResults}
      itemsCount={itemsRef?.current?.length || 0}
      suppressResultsNotification />
  );
};

export default ImportDevicesContainer;
